/**
 * @module containers/ConfirmationPage
 * @description Confirmation page displayed after successful form submission
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ConfirmationView } from 'platform/forms-system/src/js/components/ConfirmationView';
import DownloadFormPDF from '../../components/confirmation-page/download-form-pdf';

/**
 * Custom submission alert component that shows warning for additional steps needed
 * @returns {React.ReactElement} Warning alert component
 */
const CustomSubmissionAlert = () => {
  return (
    <va-alert
      uswds
      status="warning"
      className="confirmation-submission-alert-section vads-u-margin-bottom--4"
    >
      <h2 slot="headline" tabIndex="-1" className="vads-u-font-size--h3">
        You have completed half of form 21-2680. Additional steps are needed
        from a medical provider.
      </h2>
      <p className="vads-u-margin-bottom--0">
        You completed half of this form. The application is NOT submitted yet. A
        medical provider must complete the remaining sections of the form.
      </p>
    </va-alert>
  );
};

/**
 * Custom what's next section with step-by-step instructions
 * @returns {React.ReactElement} What's next section
 */
const WhatsNextSection = ({ guid, veteranName }) => {
  return (
    <div className="confirmation-whats-next-section">
      <h2>What you need to do next</h2>
      <p>Follow these steps to complete the application:</p>
      <va-process-list uswds>
        <va-process-list-item header="Send this form to a medical provider">
          <p>Download the form you just filled out. You can either:</p>
          <ul className="vads-u-margin-bottom--0">
            <li>
              send this form to a medical provider via a secure messaging
              platform, like myHealtheVet or a private provider’s medical portal
              OR
            </li>
            <li>
              print the form and bring it to the medical provider in person
            </li>
          </ul>
          {guid && <DownloadFormPDF guid={guid} veteranName={veteranName} />}
        </va-process-list-item>
        <va-process-list-item header="Have a medical provider complete the remaining sections">
          <p>
            The medical provider must be a Medical Doctor (MD) or Doctor of
            Osteopathic (DO) medicine, physician assistant or advanced practice
            registered nurse.
          </p>
        </va-process-list-item>

        <va-process-list-item header="Once the medical provider has completed their part of the form and signed it, they'll return it to you" />

        <va-process-list-item header="Upload your fully completed form">
          <p>
            <va-link-action
              href="/forms/upload/21-2680/introduction"
              text="Upload your completed VA form 21-2680"
            />
          </p>
        </va-process-list-item>
      </va-process-list>
    </div>
  );
};

WhatsNextSection.propTypes = {
  guid: PropTypes.string,
  veteranName: PropTypes.object,
};

/**
 * Confirmation page component for VA Form 21-2680
 * Displays submission confirmation details and next steps
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Object} [props.form] - Form state object from Redux
 * @param {Object} [props.form.data] - Form data
 * @param {string} [props.form.formId] - Unique form identifier
 * @param {Object} [props.form.submission] - Submission details
 * @param {string} [props.form.submission.timestamp] - Submission timestamp
 * @param {string} [props.name] - Applicant name
 * @param {Object} props.route - Route configuration
 * @param {Object} props.route.formConfig - Form configuration object
 * @returns {React.ReactElement} Confirmation page view
 */
export const ConfirmationPage = ({ route }) => {
  const form = useSelector(state => state.form || {});
  const submission = form?.submission || {};
  const submitDate = submission?.timestamp || '';

  // Extract GUID/confirmation number from submission response
  const guid =
    submission?.response?.attributes?.guid ||
    submission?.response?.attributes?.confirmationNumber ||
    '';

  const confirmationNumber =
    submission?.response?.attributes?.confirmationNumber || '';

  // Extract veteran name for PDF filename
  const veteranName = form?.data?.claimantInformation?.claimantFullName || {};

  return (
    <ConfirmationView
      formConfig={route?.formConfig}
      submitDate={submitDate}
      confirmationNumber={confirmationNumber}
      devOnly={{
        showButtons: true,
      }}
    >
      <CustomSubmissionAlert />
      <div data-dd-privacy="mask" data-dd-action-name="confirmation summary">
        <ConfirmationView.ChapterSectionCollection />
      </div>
      <WhatsNextSection guid={guid} veteranName={veteranName} />
      <ConfirmationView.HowToContact />
      <ConfirmationView.PrintThisPage />
      <ConfirmationView.GoBackLink />
      <ConfirmationView.NeedHelp />
    </ConfirmationView>
  );
};

ConfirmationPage.propTypes = {
  form: PropTypes.shape({
    data: PropTypes.object,
    formId: PropTypes.string,
    submission: PropTypes.shape({
      timestamp: PropTypes.string,
    }),
  }),
  name: PropTypes.string,
  route: PropTypes.shape({
    formConfig: PropTypes.object,
  }),
};
